import { useEffect, useRef } from "react";

import { Box, Stack, Typography } from "@mui/material";

import { useIsMobile } from "hooks/useIsMobile";

import { UnifiedPartnerSelector } from "./components/UnifiedPartnerSelector";
import { VisibilityRadioGroup } from "./components/VisibilityRadioGroup/VisibilityRadioGroup";

import type { UnifiedPartnerSelectorRef } from "./components/UnifiedPartnerSelector";
import type { ShipmentVisibility } from "./components/VisibilityRadioGroup/VisibilityRadioGroup";
import type { NetworkGroup, NetworkMember } from "./types";

interface ConsolidatedShipmentVisibilityProps {
  // Visibility state
  shipmentVisibility: ShipmentVisibility;
  onShipmentVisibilityChange: (visibility: ShipmentVisibility) => void;

  // Partner selection state
  selectedConfirmedPartnerEmails: string[];
  selectedGroups: string[];
  selectedNetworkInviteEmails: string[];
  isAllPartnersSelected: boolean;
  onConfirmedPartnerEmailsChange: (emails: string[]) => void;
  onSelectedGroupsChange: (groups: string[]) => void;
  onNetworkInviteEmailsChange: (emails: string[]) => void;
  onAllPartnersSelectedChange: (isSelected: boolean) => void;

  // Data
  confirmedPartners: NetworkMember[];
  availableGroups: NetworkGroup[];
  pendingInviteEmails: string[];

  // UI state
  loadingPartners?: boolean;
  loadingGroups?: boolean;
  loadingPendingInvites?: boolean;
  partnersError?: string | null;
  groupsError?: string | null;
  isProject?: boolean;

  // Email input ref
  onEmailInputRef?: (ref: UnifiedPartnerSelectorRef) => void;
}

export function ConsolidatedShipmentVisibility(
  props: ConsolidatedShipmentVisibilityProps,
) {
  const {
    shipmentVisibility,
    onShipmentVisibilityChange,
    selectedConfirmedPartnerEmails,
    selectedGroups,
    selectedNetworkInviteEmails,
    isAllPartnersSelected,
    onConfirmedPartnerEmailsChange,
    onSelectedGroupsChange,
    onNetworkInviteEmailsChange,
    onAllPartnersSelectedChange,
    confirmedPartners,
    availableGroups,
    pendingInviteEmails,
    loadingPartners = false,
    loadingGroups = false,
    loadingPendingInvites = false,
    partnersError = null,
    groupsError = null,
    isProject = false,
    onEmailInputRef,
  } = props;

  const useColumnLayout = useIsMobile();
  const emailInputRef = useRef<UnifiedPartnerSelectorRef>(null);

  // Expose the ref to parent component
  useEffect(() => {
    if (emailInputRef.current && onEmailInputRef) {
      onEmailInputRef(emailInputRef.current);
    }
  }, [onEmailInputRef]);

  // On private shipments a group grants access; on public shipments a group is a
  // shortcut for notifying all its members. Groups are selectable in both cases.
  const allowGroups = true;

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="h6">
        {isProject ? "Project Visibility" : "Shipment Visibility"}
      </Typography>

      <Stack
        direction={useColumnLayout ? "column" : "row"}
        spacing={useColumnLayout ? 2 : 4}
        alignItems="flex-start"
      >
        <Box sx={{ minWidth: 200 }}>
          <VisibilityRadioGroup
            visibility={shipmentVisibility}
            onVisibilityChange={onShipmentVisibilityChange}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <UnifiedPartnerSelector
            ref={emailInputRef}
            selectedConfirmedPartnerEmails={selectedConfirmedPartnerEmails}
            selectedGroups={selectedGroups}
            selectedNetworkInviteEmails={selectedNetworkInviteEmails}
            isAllPartnersSelected={isAllPartnersSelected}
            onConfirmedPartnerEmailsChange={onConfirmedPartnerEmailsChange}
            onSelectedGroupsChange={onSelectedGroupsChange}
            onNetworkInviteEmailsChange={onNetworkInviteEmailsChange}
            onAllPartnersSelectedChange={onAllPartnersSelectedChange}
            confirmedPartners={confirmedPartners}
            availableGroups={availableGroups}
            pendingInviteEmails={pendingInviteEmails}
            allowGroups={allowGroups}
            loadingPartners={loadingPartners}
            loadingGroups={loadingGroups}
            loadingPendingInvites={loadingPendingInvites}
            partnersError={partnersError}
            groupsError={groupsError}
            shipmentVisibility={shipmentVisibility}
          />
        </Box>
      </Stack>
    </Stack>
  );
}
